// NLDC grid-frequency chart with the IEGC band (49.90 / 50.000 / 50.05 Hz)
// drawn as reference lines + a tinted in-band region. Out-of-band points read
// red. Shared by the Dashboard footer, Market intelligence rail and the Trading
// frequency panel.

import { memo } from "react";
import {
  Area,
  AreaChart,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { ChartFrame } from "@/components/charts/ChartFrame";
import { usePhone } from "@/app/console/useViewport";
import {
  ANIMATION_ACTIVE,
  AXIS_TICK,
  GRID_PROPS,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_LABEL_STYLE,
} from "@/components/charts/theme";

export const FREQ_REF = 50.0;
export const FREQ_BAND_LO = 49.9;
export const FREQ_BAND_HI = 50.05;

export interface FreqPoint {
  t: string;
  hz: number;
  [key: string]: unknown;
}

export interface FrequencyBandChartProps {
  data: ReadonlyArray<FreqPoint>;
  height?: number;
  compact?: boolean;
  domain?: [number, number];
  /** Full-screen heading (defaults to the enclosing Card's title). */
  title?: string;
  /** Expand affordance (default on; compact strips pass false). */
  expandable?: boolean;
}

const hzTick = (v: number) => v.toFixed(3);
const timeTick = (t: unknown) => {
  const d = new Date(String(t));
  return Number.isNaN(d.getTime()) ? String(t) : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};
const freqTooltip = (v: unknown) => [`${Number(v).toFixed(3)} Hz`, "Frequency"] as [string, string];

function FrequencyBandChartBase({ data, height = 200, compact = false, domain = [49.85, 50.15], title, expandable }: FrequencyBandChartProps) {
  const phone = usePhone();
  return (
    <ChartFrame height={height} title={title ?? "Grid frequency"} expandable={expandable ?? !compact}>
      <AreaChart data={data as FreqPoint[]} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="freqGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.18} />
            <stop offset="100%" stopColor="#60A5FA" stopOpacity={0} />
          </linearGradient>
        </defs>
        {!compact && <CartesianGrid {...GRID_PROPS} />}
        <XAxis dataKey="t" tick={compact ? false : AXIS_TICK} tickLine={false} minTickGap={phone ? 64 : 48} interval={phone ? "preserveStartEnd" : undefined} tickFormatter={timeTick} hide={compact} />
        <YAxis domain={domain} tick={AXIS_TICK} tickLine={false} width={48} tickFormatter={hzTick} />
        <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} formatter={freqTooltip as never} labelFormatter={timeTick as never} />
        <ReferenceArea y1={FREQ_BAND_LO} y2={FREQ_BAND_HI} fill="#34d399" fillOpacity={0.05} />
        <ReferenceLine y={FREQ_REF} stroke="#3f3f46" strokeDasharray="2 2" />
        <ReferenceLine y={FREQ_BAND_LO} stroke="#f87171" strokeOpacity={0.5} strokeDasharray="4 4" />
        <ReferenceLine y={FREQ_BAND_HI} stroke="#f87171" strokeOpacity={0.5} strokeDasharray="4 4" />
        <Area dataKey="hz" stroke="#60A5FA" strokeWidth={1.6} fill="url(#freqGrad)" dot={false} isAnimationActive={ANIMATION_ACTIVE} />
      </AreaChart>
    </ChartFrame>
  );
}

export function freqStats(points: ReadonlyArray<FreqPoint>): {
  current: number;
  min: number;
  max: number;
  avg: number;
  inBand: boolean;
  excursions: number;
} {
  if (points.length === 0) return { current: FREQ_REF, min: FREQ_REF, max: FREQ_REF, avg: FREQ_REF, inBand: true, excursions: 0 };
  const hz = points.map((p) => p.hz);
  const current = hz[hz.length - 1]!;
  let min = hz[0]!;
  let max = hz[0]!;
  let sum = 0;
  let excursions = 0;
  for (const v of hz) {
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
    if (v < FREQ_BAND_LO || v > FREQ_BAND_HI) excursions++;
  }
  return {
    current,
    min,
    max,
    avg: sum / hz.length,
    inBand: current >= FREQ_BAND_LO && current <= FREQ_BAND_HI,
    excursions,
  };
}

export const FrequencyBandChart = memo(FrequencyBandChartBase);
